import React, { useState } from 'react';
import axios from 'axios';
import PriceChart from './PriceChart';
import {
    Card, Row, Col, Form, Button, Alert, Spinner, Badge, Table,
    ListGroup, ProgressBar
} from 'react-bootstrap';
import { Search, ArrowUpShort, ArrowDownShort, Calendar, CurrencyDollar, BarChart } from 'react-bootstrap-icons';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const StockResearchTab = () => {
    const [tickerSymbol, setTickerSymbol] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [researchData, setResearchData] = useState(null);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!tickerSymbol.trim()) {
            setError('Please enter a ticker symbol');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setResearchData(null);

            const response = await axios.get(
                `${API_BASE_URL}/api/stocks/research/${tickerSymbol.toUpperCase()}`
            );

            setResearchData(response.data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching research data:', err);
            setError(err.response?.data?.detail || 'Failed to fetch research data');
            setLoading(false);
        }
    };

    const formatNumber = (num, decimals = 2) => {
        if (num === null || num === undefined) return 'N/A';
        return num.toLocaleString(undefined, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    const formatCurrency = (num) => {
        if (num === null || num === undefined) return 'N/A';
        return `$${formatNumber(num, 2)}`;
    };

    const formatPercent = (num) => {
        if (num === null || num === undefined) return 'N/A';
        return `${formatNumber(num, 2)}%`;
    };

    const formatMarketCap = (num) => {
        if (num === null || num === undefined) return 'N/A';
        if (num >= 1000) return `$${formatNumber(num / 1000, 2)}T`;
        return `$${formatNumber(num, 2)}B`;
    };

    const getRecommendationBadge = (rating) => {
        const ratingMap = {
            1: { variant: 'success', text: 'Strong Buy' },
            2: { variant: 'success', text: 'Buy' },
            3: { variant: 'warning', text: 'Hold' },
            4: { variant: 'danger', text: 'Sell' },
            5: { variant: 'danger', text: 'Strong Sell' }
        };
        const rec = ratingMap[rating] || { variant: 'secondary', text: 'N/A' };
        return <Badge bg={rec.variant}>{rec.text}</Badge>;
    };

    const renderPriceChange = (change) => {
        if (!change) return <span className="text-muted">N/A</span>;
        const isPositive = change >= 0;
        const Icon = isPositive ? ArrowUpShort : ArrowDownShort;
        const color = isPositive ? 'success' : 'danger';
        return (
            <span className={`text-${color}`}>
                <Icon className="me-1" />
                {formatPercent(change)}
            </span>
        );
    };

    return (
        <div className="stock-research-tab p-4">
            {/* Search Section */}
            <Card className="mb-4">
                <Card.Body>
                    <Form onSubmit={handleSearch}>
                        <Row className="align-items-end">
                            <Col md={8}>
                                <Form.Group>
                                    <Form.Label><strong>Research a Stock</strong></Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter ticker symbol (e.g., AAPL, GOOGL, MSFT)"
                                        value={tickerSymbol}
                                        onChange={(e) => setTickerSymbol(e.target.value.toUpperCase())}
                                        size="lg"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={loading}
                                    size="lg"
                                    className="w-100"
                                >
                                    {loading ? <Spinner animation="border" size="sm" /> : <><Search className="me-2" />Research</>}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* Error Alert */}
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Research Data */}
            {researchData && (
                <div>
                    {/* Company Overview */}
                    <Card className="mb-4">
                        <Card.Header className="bg-primary text-white">
                            <h4 className="mb-0">
                                <BarChart className="me-2" />
                                Company Overview
                            </h4>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={8}>
                                    <h3>{researchData.profile?.company_name || researchData.ticker}</h3>
                                    <p className="text-muted mb-3">
                                        {researchData.ticker} • {researchData.profile?.exchange} • {researchData.profile?.currency}
                                    </p>
                                    <p><strong>Sector:</strong> {researchData.profile?.sector || 'N/A'}</p>
                                    <p><strong>Industry:</strong> {researchData.profile?.industry || 'N/A'}</p>
                                </Col>
                                <Col md={4} className="text-end">
                                    <div className="mb-3">
                                        <h1 className="display-4">
                                            {formatCurrency(researchData.quote?.price)}
                                        </h1>
                                        <div className="h5">
                                            {renderPriceChange(researchData.quote?.price_change_percentage)}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Card className="mb-4">
                        <Card.Header className="bg-primary text-white">
                            <h4 className="mb-0">
                                <BarChart className="me-2" />
                                Price Chart
                            </h4>
                        </Card.Header>
                        <Card.Body>
                            <PriceChart tickerSymbol={researchData.ticker} />
                        </Card.Body>
                    </Card>

                    <Row>
                        {/* Key Statistics */}
                        <Col lg={6} className="mb-4">
                            <Card className="h-100">
                                <Card.Header className="bg-info text-white">
                                    <h5 className="mb-0">Key Statistics</h5>
                                </Card.Header>
                                <Card.Body>
                                    <Table bordered size="sm">
                                        <tbody>
                                            <tr>
                                                <td><strong>Market Cap</strong></td>
                                                <td className="text-end">{formatMarketCap(researchData.financials?.market_cap)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>P/E Ratio</strong></td>
                                                <td className="text-end">{formatNumber(researchData.financials?.pe_ratio, 2)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>EPS (TTM)</strong></td>
                                                <td className="text-end">{formatCurrency(researchData.financials?.eps)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Beta</strong></td>
                                                <td className="text-end">{formatNumber(researchData.financials?.beta, 2)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Dividend Yield</strong></td>
                                                <td className="text-end">{formatPercent(researchData.financials?.dividend_yield)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>52-Week High</strong></td>
                                                <td className="text-end">{formatCurrency(researchData.financials?.['52_week_high'])}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>52-Week Low</strong></td>
                                                <td className="text-end">{formatCurrency(researchData.financials?.['52_week_low'])}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Valuation Metrics */}
                        <Col lg={6} className="mb-4">
                            <Card className="h-100">
                                <Card.Header className="bg-success text-white">
                                    <h5 className="mb-0">Valuation Metrics</h5>
                                </Card.Header>
                                <Card.Body>
                                    <Table bordered size="sm">
                                        <tbody>
                                            <tr>
                                                <td><strong>P/E Ratio</strong></td>
                                                <td className="text-end">{formatNumber(researchData.financials?.pe_ratio, 2)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>P/B Ratio</strong></td>
                                                <td className="text-end">{formatNumber(researchData.financials?.pb_ratio, 2)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>P/S Ratio</strong></td>
                                                <td className="text-end">{formatNumber(researchData.financials?.ps_ratio, 2)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>PEG Ratio</strong></td>
                                                <td className="text-end">{formatNumber(researchData.financials?.peg_ratio, 2)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Price/Book</strong></td>
                                                <td className="text-end">{formatNumber(researchData.financials?.pb_ratio, 2)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Book Value/Share</strong></td>
                                                <td className="text-end">{formatCurrency(researchData.financials?.book_value_per_share)}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Profitability Metrics */}
                        <Col lg={6} className="mb-4">
                            <Card className="h-100">
                                <Card.Header className="bg-warning text-dark">
                                    <h5 className="mb-0">Profitability</h5>
                                </Card.Header>
                                <Card.Body>
                                    <Table bordered size="sm">
                                        <tbody>
                                            <tr>
                                                <td><strong>ROE (Return on Equity)</strong></td>
                                                <td className="text-end">{formatPercent(researchData.financials?.roe)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>ROA (Return on Assets)</strong></td>
                                                <td className="text-end">{formatPercent(researchData.financials?.roa)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>ROI (Return on Investment)</strong></td>
                                                <td className="text-end">{formatPercent(researchData.financials?.roi)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Gross Margin</strong></td>
                                                <td className="text-end">{formatPercent(researchData.financials?.gross_margin)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Operating Margin</strong></td>
                                                <td className="text-end">{formatPercent(researchData.financials?.operating_margin)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Net Profit Margin</strong></td>
                                                <td className="text-end">{formatPercent(researchData.financials?.net_margin)}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Growth Metrics */}
                        <Col lg={6} className="mb-4">
                            <Card className="h-100">
                                <Card.Header className="bg-secondary text-white">
                                    <h5 className="mb-0">Growth Metrics</h5>
                                </Card.Header>
                                <Card.Body>
                                    <Table bordered size="sm">
                                        <tbody>
                                            <tr>
                                                <td><strong>Revenue Growth (3Y)</strong></td>
                                                <td className="text-end">{formatPercent(researchData.financials?.revenue_growth_3y)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Revenue Growth (5Y)</strong></td>
                                                <td className="text-end">{formatPercent(researchData.financials?.revenue_growth_5y)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>EPS Growth (3Y)</strong></td>
                                                <td className="text-end">{formatPercent(researchData.financials?.eps_growth_3y)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>EPS Growth (5Y)</strong></td>
                                                <td className="text-end">{formatPercent(researchData.financials?.eps_growth_5y)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Dividend Per Share</strong></td>
                                                <td className="text-end">{formatCurrency(researchData.financials?.dividend_per_share)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Payout Ratio</strong></td>
                                                <td className="text-end">{formatPercent(researchData.financials?.payout_ratio)}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Analyst Recommendations */}
                    {researchData.recommendations && researchData.recommendations.length > 0 && (
                        <Card className="mb-4">
                            <Card.Header className="bg-dark text-white">
                                <h5 className="mb-0">Analyst Recommendations</h5>
                            </Card.Header>
                            <Card.Body>
                                <Table responsive>
                                    <thead>
                                        <tr>
                                            <th>Period</th>
                                            <th className="text-center">Strong Buy</th>
                                            <th className="text-center">Buy</th>
                                            <th className="text-center">Hold</th>
                                            <th className="text-center">Sell</th>
                                            <th className="text-center">Strong Sell</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {researchData.recommendations.slice(0, 3).map((rec, idx) => (
                                            <tr key={idx}>
                                                <td>{rec.period}</td>
                                                <td className="text-center"><Badge bg="success">{rec.strongBuy || 0}</Badge></td>
                                                <td className="text-center"><Badge bg="success">{rec.buy || 0}</Badge></td>
                                                <td className="text-center"><Badge bg="warning">{rec.hold || 0}</Badge></td>
                                                <td className="text-center"><Badge bg="danger">{rec.sell || 0}</Badge></td>
                                                <td className="text-center"><Badge bg="danger">{rec.strongSell || 0}</Badge></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Price Target */}
                    {researchData.price_target && (
                        <Card className="mb-4">
                            <Card.Header className="bg-primary text-white">
                                <h5 className="mb-0">
                                    <CurrencyDollar className="me-2" /> {/* Replaced DollarSign */}
                                    Analyst Price Target
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row className="text-center">
                                    <Col md={3}>
                                        <h6 className="text-muted">Low</h6>
                                        <h4 className="text-danger">{formatCurrency(researchData.price_target.targetLow)}</h4>
                                    </Col>
                                    <Col md={3}>
                                        <h6 className="text-muted">Average</h6>
                                        <h4>{formatCurrency(researchData.price_target.targetMean)}</h4>
                                    </Col>
                                    <Col md={3}>
                                        <h6 className="text-muted">Median</h6>
                                        <h4>{formatCurrency(researchData.price_target.targetMedian)}</h4>
                                    </Col>
                                    <Col md={3}>
                                        <h6 className="text-muted">High</h6>
                                        <h4 className="text-success">{formatCurrency(researchData.price_target.targetHigh)}</h4>
                                    </Col>
                                </Row>
                                {researchData.quote?.price && researchData.price_target.targetMean && (
                                    <div className="mt-3">
                                        <p className="text-center">
                                            <strong>Upside Potential:</strong>
                                            <span className={researchData.price_target.targetMean > researchData.quote.price ? 'text-success ms-2' : 'text-danger ms-2'}>
                                                {formatPercent(((researchData.price_target.targetMean - researchData.quote.price) / researchData.quote.price) * 100)}
                                            </span>
                                        </p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Peer Comparison */}
                    {researchData.peers && researchData.peers.length > 0 && (
                        <Card className="mb-4">
                            <Card.Header className="bg-info text-white">
                                <h5 className="mb-0">Peer Companies</h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="d-flex flex-wrap gap-2">
                                    {researchData.peers.slice(0, 10).map((peer, idx) => (
                                        <Badge key={idx} bg="secondary" className="p-2">{peer}</Badge>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Company News */}
                    {researchData.news && researchData.news.length > 0 && (
                        <Card className="mb-4">
                            <Card.Header className="bg-dark text-white">
                                <h5 className="mb-0">
                                    <Calendar className="me-2" />
                                    Recent News
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <ListGroup variant="flush">
                                    {researchData.news.map((article, idx) => (
                                        <ListGroup.Item key={idx}>
                                            <Row>
                                                <Col md={2}>
                                                    {article.image && (
                                                        <img src={article.image} alt="" className="img-fluid rounded" />
                                                    )}
                                                </Col>
                                                <Col md={10}>
                                                    <h6>
                                                        <a href={article.url} target="_blank" rel="noopener noreferrer">
                                                            {article.headline}
                                                        </a>
                                                    </h6>
                                                    <p className="text-muted small mb-1">{article.summary}</p>
                                                    <small className="text-muted">
                                                        {article.source} • {new Date(article.datetime * 1000).toLocaleDateString()}
                                                    </small>
                                                </Col>
                                            </Row>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Trading Decision Helper */}
                    <Card className="mb-4 border-primary">
                        <Card.Header className="bg-gradient text-white" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
                            <h5 className="mb-0">Quick Trading Decision Summary</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <h6 className="text-success">Bullish Indicators:</h6>
                                    <ul>
                                        {researchData.financials?.revenue_growth_3y > 10 && (
                                            <li>Strong revenue growth (3Y: {formatPercent(researchData.financials.revenue_growth_3y)})</li>
                                        )}
                                        {researchData.financials?.roe > 15 && (
                                            <li>High ROE ({formatPercent(researchData.financials.roe)})</li>
                                        )}
                                        {researchData.financials?.net_margin > 10 && (
                                            <li>Healthy profit margins ({formatPercent(researchData.financials.net_margin)})</li>
                                        )}
                                        {researchData.price_target && researchData.quote && researchData.price_target.targetMean > researchData.quote.price && (
                                            <li>Analyst target above current price (Upside: {formatPercent(((researchData.price_target.targetMean - researchData.quote.price) / researchData.quote.price) * 100)})</li>
                                        )}
                                    </ul>
                                </Col>
                                <Col md={6}>
                                    <h6 className="text-danger">Bearish Indicators:</h6>
                                    <ul>
                                        {researchData.financials?.revenue_growth_3y < 0 && (
                                            <li>Negative revenue growth ({formatPercent(researchData.financials.revenue_growth_3y)})</li>
                                        )}
                                        {researchData.financials?.pe_ratio > 30 && (
                                            <li>High P/E ratio ({formatNumber(researchData.financials.pe_ratio, 2)})</li>
                                        )}
                                        {researchData.financials?.net_margin < 5 && (
                                            <li>Low profit margins ({formatPercent(researchData.financials.net_margin)})</li>
                                        )}
                                        {researchData.price_target && researchData.quote && researchData.price_target.targetMean < researchData.quote.price && (
                                            <li>Analyst target below current price</li>
                                        )}
                                    </ul>
                                </Col>
                            </Row>
                            <Alert variant="info" className="mt-3">
                                <strong>Note:</strong> This is automated analysis based on fundamental data. Always conduct your own research and consider your risk tolerance before making investment decisions.
                            </Alert>
                        </Card.Body>
                    </Card>
                </div>
            )}

            {!loading && !researchData && !error && (
                <Card className="text-center p-5">
                    <Card.Body>
                        <Search size={64} className="text-muted mb-3" />
                        <h4 className="text-muted">Search for a stock to view research details</h4>
                        <p className="text-muted">
                            Enter a ticker symbol above to get comprehensive research data including financials, analyst ratings, news, and more.
                        </p>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};

export default StockResearchTab;
